const { DASHBOARD: Types } = require('../action-types');
const { combineReducers } = require('redux');

const internals = {};

const userStats = (state = null, action) => {

    switch (action.type) {
        case Types.GET_USER_STATS.SUCCESS: {

            if (!action.payload?.result || !action.payload?.request) {
                return null;
            }

            const { context } = require('app-context');

            const redux = context.redux.hooks;
            const appSelectors = context.selectors.all;

            // Fill in missing weeks and values
            action.payload.result.growthStats.userGrowth = internals.fillWeeklyCarry(action.payload.result.growthStats.userGrowth);
            action.payload.result.growthStats.messageGrowth = internals.fillWeeklyCarry(action.payload.result.growthStats.messageGrowth);
            action.payload.result.growthStats.connectionGrowth = internals.fillWeeklyCarry(action.payload.result.growthStats.connectionGrowth);

            [].concat(action.payload.result.interests)
                .concat(action.payload.result.passionInterests)
                .concat(action.payload.result.customInterests)
                .forEach((interest) => {

                    if (!interest) {
                        return;
                    }

                    interest.svg = appSelectors.getInterestSVGById(
                        redux.getState(),
                        interest.id
                    );
                });

            // Don't overwrite incomingClass array if filtering on a specific incomingClass
            if (action.payload.request.incomingClass) {
                return {
                    ...action.payload.result,
                    incomingClass: state.incomingClass
                };
            }

            return action.payload.result || null;
        }

        default:
            return state;
    }
};

const messagesStats = (state = null, action) => {

    switch (action.type) {
        case Types.GET_MESSAGES_STATS.SUCCESS:
            return action.payload?.result || null;
        default:
            return state;
    }
};

const reducer = combineReducers({
    userStats,
    messagesStats
});

const selectors = {
    getUserStats: (state) => state.userStats,
    getMessagesStats: (state) => state.messagesStats
};

module.exports = Object.assign(reducer, { selectors });

internals.fillMonthlyCarry = (dataByMonth) => {

    // Collect all possible role keys
    const allRoles = new Set();

    for (const { roles } of Object.values(dataByMonth)) {
        if (roles) {
            Object.keys(roles).forEach((role) => allRoles.add(role));
        }
    }

    // Init lastSeen to zeros for every key
    const lastSeen = {};
    allRoles.forEach((role) => {

        lastSeen[role] = 0;
    });

    // Get first month from the data after sorting
    const sortedDates = Object.keys(dataByMonth).sort((a, b) => {

        if (new Date(a) < new Date(b)) {
            return -1;
        }

        return 1;
    });

    const now = new Date();

    // Go back a min of 4 months
    const monthsBack = Math.max(4, internals.monthDiff(new Date(sortedDates[0]), now));

    // Compute the 1st of month, `monthsBack` ago (UTC)
    const start = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth() - monthsBack,
        1
    ));

    const newMonthsData = [];
    // Walk each month
    for (let i = 0; i <= monthsBack; ++i) {

        const dt  = new Date(Date.UTC(
            start.getUTCFullYear(),
            start.getUTCMonth() + i,
            1
        ));

        const iso = dt.toISOString().replace(/\.\d+Z$/, `.000Z`);
        const entry = dataByMonth[iso];

        // Update any roles present this month
        if (entry?.roles) {
            for (const [role, val] of Object.entries(entry.roles)) {
                lastSeen[role] = val;
            }
        }

        // Snapshot: every key has either its lastSeen value (or default 0)
        newMonthsData.push({
            date: iso,
            roles: { ...lastSeen }
        });
    }

    return newMonthsData.reduce((collector, item) => {

        collector[item.date] = item;

        return collector;
    }, {});
};

internals.monthDiff = (d1, d2) => {

    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
};

internals.fillWeeklyCarry = (dataByWeek) => {

    // Collect all possible role keys
    const allRoles = new Set();

    for (const { roles } of Object.values(dataByWeek)) {
        if (roles) {
            Object.keys(roles).forEach((role) => allRoles.add(role));
        }
    }

    // Init lastSeen to zeros for every key
    const lastSeen = {};
    allRoles.forEach((role) => {

        lastSeen[role] = 0;
    });

    // Get first week from the data after sorting
    const sortedDates = Object.keys(dataByWeek).sort((a, b) => {

        if (new Date(a) < new Date(b)) {
            return -1;
        }

        return 1;
    });

    const now = new Date();

    // Go back a min of 16 weeks (about 4 months)
    const weeksBack = Math.max(16,internals.weekDiff(new Date(sortedDates[0]), now));

    // Compute the start of week, `weeksBack` ago (UTC).
    const start = new Date(now);
    const dayOfWeek = start.getUTCDay(); // Sunday=0, Monday=1, etc.
    const daysToMonday = (dayOfWeek === 0) ? 6 : (dayOfWeek - 1); // Convert Sunday=0 to Monday=0 base like PostgreSQL
    start.setUTCDate(start.getUTCDate() - daysToMonday - (7 * weeksBack));
    start.setUTCHours(0, 0, 0, 0);

    const newWeeksData = [];
    // Walk each week
    for (let i = 0; i <= weeksBack; ++i) {

        const dt = new Date(start);
        dt.setUTCDate(start.getUTCDate() + (i * 7));

        const iso = dt.toISOString().replace(/\.\d+Z$/, `.000Z`);
        const entry = dataByWeek[iso];

        // Update any roles present this week
        if (entry?.roles) {
            for (const [role, val] of Object.entries(entry.roles)) {
                lastSeen[role] = val;
            }
        }

        // Snapshot: every key has either its lastSeen value (or default 0)
        newWeeksData.push({
            date: iso,
            roles: { ...lastSeen }
        });
    }

    return newWeeksData.reduce((collector, item) => {

        collector[item.date] = item;

        return collector;
    }, {});
};

internals.weekDiff = (d1, d2) => {

    const diffTime = Math.abs(d2 - d1);
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    return diffWeeks;
};
